import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  AssetSoftTagResponse,
  SoftTagListResponse,
  TagMappingResponse,
} from '../utils/match-maker.types';
import {
  createTagMapping,
  getTimeSeriesSoftTags,
  removeTagMapping,
  updateTagMapping,
} from './material-soft-tag.thunk';

interface MaterialSoftTagState {
  loading: boolean;
  timeSeriesSoftTags?: AssetSoftTagResponse;
  error?: string | null;
  createMappingLoading: boolean;
  createMappingData?: TagMappingResponse;
  createMappingError?: string | null;
  deleteMappingLoading: boolean;
  deleteMappingError?: string | null;
  updateMappingLoading: boolean;
  updateMappingError?: string | null;
}

const initialState: MaterialSoftTagState = {
  loading: false,
  timeSeriesSoftTags: undefined,
  error: null,
  createMappingLoading: false,
  createMappingData: undefined,
  createMappingError: null,
  deleteMappingLoading: false,
  deleteMappingError: null,
  updateMappingLoading: false,
  updateMappingError: null,
};

const materialSoftTagSlice = createSlice({
  name: 'materialSoftTag',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    clearTimeSeriesSoftTags(state) {
      state.timeSeriesSoftTags = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getTimeSeriesSoftTags.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTimeSeriesSoftTags.fulfilled,
        (state, action: PayloadAction<AssetSoftTagResponse>) => {
          state.timeSeriesSoftTags = action.payload;
          state.loading = false;
        }
      )
      .addCase(getTimeSeriesSoftTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTagMapping.pending, state => {
        state.createMappingLoading = true;
        state.createMappingError = null;
      })
      .addCase(
        createTagMapping.fulfilled,
        (state, action: PayloadAction<TagMappingResponse>) => {
          state.createMappingData = action.payload;
          state.createMappingLoading = false;
        }
      )
      .addCase(createTagMapping.rejected, (state, action) => {
        state.createMappingLoading = false;
        state.createMappingError = action.payload as string;
      })
      .addCase(removeTagMapping.pending, state => {
        state.deleteMappingLoading = true;
        state.deleteMappingError = null;
      })
      .addCase(removeTagMapping.fulfilled, state => {
        state.deleteMappingLoading = false;
      })
      .addCase(removeTagMapping.rejected, (state, action) => {
        state.deleteMappingLoading = false;
        state.deleteMappingError = action.payload as string;
      })
      .addCase(updateTagMapping.pending, state => {
        state.updateMappingLoading = true;
        state.updateMappingError = null;
      })
      .addCase(
        updateTagMapping.fulfilled,
        (state, action: PayloadAction<TagMappingResponse>) => {
          state.createMappingData = action.payload;
          state.updateMappingLoading = false;
        }
      )
      .addCase(updateTagMapping.rejected, (state, action) => {
        state.updateMappingLoading = false;
        state.updateMappingError = action.payload as string;
      });
  },
});

export const { clearTimeSeriesSoftTags } = materialSoftTagSlice.actions;
export default materialSoftTagSlice.reducer;
